import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useEOSAuth } from "../../contexts/EOSAuthContext";

export type StockRecommendation = {
  symbol?: string;
  name?: string;
  action?: string;
  confidence?: number;
  [key: string]: unknown;
};

export type AppliedModel = {
  name?: string;
  [key: string]: unknown;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://engineeringofstockbackend.onrender.com";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

export function useDashboard() {
  const { user, token } = useEOSAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  }, [token]);

  const [topStocks, setTopStocks] = useState<StockRecommendation[]>([]);
  const [loadingStocks, setLoadingStocks] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<string | null>(null);
  const [stockError, setStockError] = useState<string | null>(null);
  const [appliedModel, setAppliedModel] = useState<AppliedModel | null>(null);
  const [loadingModel, setLoadingModel] = useState(false);
  const [realBalance, setRealBalance] = useState<number | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  const handleBrokerSettings = () => navigate("/broker-selection");
  const handleViewModels = () => navigate("/models");
  const handleViewTrades = () => navigate("/trades");

  const fetchAppliedModel = async () => {
    setLoadingModel(true);
    try {
      const response = await api.get("/api/automated-trading/status");
      if (response.data.success) {
        setAppliedModel(response.data?.data?.currentModel ?? null);
      } else {
        setAppliedModel(null);
      }
    } catch {
      setAppliedModel(null);
    } finally {
      setLoadingModel(false);
    }
  };

  const fetchRealBalance = useCallback(async () => {
    if (user?.broker !== "zerodha") return;

    setLoadingBalance(true);
    try {
      const response = await api.get("/api/trading/account-balance");
      if (response.data.success) {
        setRealBalance(response.data?.data?.balance ?? null);
      }
    } catch {
      // noop
    } finally {
      setLoadingBalance(false);
    }
  }, [user?.broker]);

  const isMarketOpen = () => {
    const now = new Date();
    const istTime = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const day = istTime.getDay();
    const hour = istTime.getHours();
    const minute = istTime.getMinutes();

    if (day === 0 || day === 6) return false;

    const marketOpen = 9 * 60 + 15;
    const marketClose = 15 * 60 + 30;
    const currentTime = hour * 60 + minute;

    return currentTime >= marketOpen && currentTime <= marketClose;
  };

  const fetchTopStocks = useCallback(async () => {
    if (!isMarketOpen()) {
      setStockError("Market is currently closed");
      setLoadingStocks(false);
      return;
    }

    setLoadingStocks(true);
    setStockError(null);
    try {
      const response = await api.post("/api/ai/top-stocks");
      if (response.data.success) {
        const data = response.data;
        if (data.stocks && data.stocks.length > 0) {
          setTopStocks(data.stocks);
          setLastUpdateTime(new Date().toISOString());
        } else if (data.rawResponse) {
          setStockError(
            `Response format issue: ${data.message}. Raw: ${String(data.rawResponse).substring(0, 100)}...`,
          );
        } else {
          setStockError("No stock recommendations available at the moment");
        }
      } else {
        setStockError(response.data?.error || "Failed to fetch stock recommendations");
      }
    } catch {
      setStockError("Network error. Please check your connection.");
    } finally {
      setLoadingStocks(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMarketOpen()) {
      void fetchTopStocks();
      const interval = setInterval(() => {
        if (isMarketOpen()) {
          void fetchTopStocks();
        }
      }, 3600000);
      return () => clearInterval(interval);
    }

    setStockError("Market is currently closed");
    return undefined;
  }, [fetchTopStocks]);

  useEffect(() => {
    void fetchAppliedModel();
  }, []);

  useEffect(() => {
    if (user?.broker === "connecting") {
      const interval = setInterval(async () => {
        try {
          const response = await api.get("/api/auth/verify");
          if (response.data.success) {
            const data = response.data;
            if (data.data.user.broker !== user.broker) {
              window.location.reload();
            }
          }
        } catch {
          // noop
        }
      }, 3000);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [user?.broker]);

  useEffect(() => {
    if (user?.broker === "zerodha") {
      void fetchRealBalance();
      const interval = setInterval(() => {
        void fetchRealBalance();
      }, 30000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [user?.broker, fetchRealBalance]);

  return {
    user,
    topStocks,
    loadingStocks,
    lastUpdateTime,
    stockError,
    appliedModel,
    loadingModel,
    realBalance,
    loadingBalance,
    handleBrokerSettings,
    handleViewModels,
    handleViewTrades,
    fetchRealBalance,
    fetchTopStocks,
    isMarketOpen,
  };
}
